/**
 * One vendor payable, and everything needed to settle it.
 *
 * A payable is raised automatically the moment the Head of Stores approves a
 * goods receipt — for the accepted quantity only. Settling it is a three-step
 * trail that an auditor can follow end to end:
 *
 *   1. ENTER THE INVOICE   the vendor's tax invoice, as printed
 *   2. RECONCILE           match it against this payable; any difference
 *                          between what was received and what was billed has
 *                          to be explained in writing
 *   3. RECORD PAYMENTS     what the accounts team actually paid, when, how
 *
 * Nothing here moves money. It records what the accounts team did outside the
 * system, so procurement and the CFO can see, per vendor and per receipt,
 * what is owed and what has gone out.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, BadgeIndianRupee, Link2, Paperclip, Receipt } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, Select, StatusPill, Textarea, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, AttachmentList, FileChips, VendorPapersCard, queueAttachments,
  useAttachmentsFor, type PendingAttachment,
} from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { DocumentDate } from '@/components/docs/DocumentDate'
import { supabase } from '@/lib/supabase'
import { can, iso } from '@/lib/domain'
import { ddmmyyyy, rupees } from '@/lib/format'
import { useCompanySettings } from '@/lib/settings'

type Any = Record<string, any>

const PAYMENT_MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Demand Draft', 'Cash', 'Adjustment']

export function PayableDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role

  const [payable, setPayable] = useState<Any | null>(null)
  const [grnLines, setGrnLines] = useState<Any[]>([])
  const [recons, setRecons] = useState<Any[]>([])
  const [payments, setPayments] = useState<Any[]>([])
  // 0229: paid through Project Finance (a payment requisition, or a Finance advance used up here).
  const [finPays, setFinPays] = useState<Any[]>([])
  const [openInvoices, setOpenInvoices] = useState<Any[]>([])
  const [invoiceFiles, setInvoiceFiles] = useState<Record<string, Any[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [invoiceOpen, setInvoiceOpen] = useState(false)
  const [reconOpen, setReconOpen] = useState(false)
  const [reconPreset, setReconPreset] = useState<string>('')
  const [payOpen, setPayOpen] = useState<Any | null>(null)

  const [tolerance, setTolerance] = useState(5)
  // When a payable counts as overdue: a System Setting since 0184 (45 days).
  const { payableOverdueDays } = useCompanySettings()
  const [closeOpen, setCloseOpen] = useState(false)
  const [closeReason, setCloseReason] = useState('')

  /** Declare that the vendor has finished billing this receipt — or reopen it. */
  async function closeInvoicing(close: boolean, reason?: string) {
    setBusy(true)
    try {
      const { error: e } = await supabase.rpc('rpc_close_payable_invoicing', {
        p_payable_id: id, p_closed: close, p_reason: reason ?? null,
      })
      if (e) throw new Error(e.message)
      toast.success(close
        ? 'Billing closed. The amount payable is now the invoiced figure.'
        : 'Billing reopened.')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not change the billing status')
    } finally { setBusy(false) }
  }

  /** Open an invoice scan in a new tab. */
  async function openFile(path: string) {
    const { data, error: e } = await supabase.storage
      .from('doc-attachments').createSignedUrl(path, 3600)
    if (e || !data) { toast.error('That file could not be opened.'); return }
    window.open(data.signedUrl, '_blank', 'noopener')
  }

  // Booking a vendor's invoice, matching it and recording the payment are
  // accounts-payable duties. Accounts joins Procurement on all of them.
  const maySettle = can(role, 'recordInvoice')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: p, error: pErr } = await supabase
        .from('v_payable_settlement').select('*').eq('id', id).maybeSingle()
      if (pErr) throw new Error(pErr.message)
      if (!p) throw new Error(`Payable ${id} was not found, or you do not have access to it.`)
      setPayable(p)

      // What the payable is actually for — the accepted lines on the receipt.
      if (p.grn_id) {
        const { data: gl } = await supabase.from('grn_line_items')
          .select('*, materials(item_name, item_code)').eq('grn_id', p.grn_id).order('sl_no')
        setGrnLines(gl ?? [])
      }

      const { data: rc } = await supabase.from('invoice_reconciliations')
        .select('*, vendor_invoices(id, invoice_no, invoice_date, total_amount, taxable_amount, gst_amount, status)')
        .eq('payable_id', id)
      setRecons(rc ?? [])

      // Money reaches a payable by TWO routes, and this card used to know about
      // only one of them.
      //
      //   1. The usual one — the vendor bills, the invoice is matched to this
      //      payable, and the payment is made against that invoice.
      //   2. Straight onto the payable, with no invoice in between. A vendor
      //      advance applied here, or the site's petty cash on an ad-hoc
      //      receipt. Those are linked through advance_adjustments.
      //
      // Only route 1 was being read. So a petty-cash purchase showed
      // "Settled in full" in one card and "Nothing paid" in the card directly
      // below it — the money was recorded correctly and simply not looked for.
      const invoiceIds = (rc ?? []).map((r: Any) => r.invoice_id)

      const { data: adj } = await supabase.from('advance_adjustments')
        .select('payment_id, amount').eq('payable_id', id)
      // How much of each payment landed on THIS payable. One advance can be
      // spread over several, so the payment's own total would overstate it.
      const appliedHere = new Map<string, number>(
        (adj ?? []).map((a: Any) => [a.payment_id, Number(a.amount)]))

      const byId = new Map<string, Any>()
      if (invoiceIds.length) {
        const { data: pm } = await supabase.from('vendor_payments')
          .select('*').in('invoice_id', invoiceIds)
        for (const p of pm ?? []) byId.set(p.id, p)
      }
      if (appliedHere.size) {
        const { data: pm } = await supabase.from('vendor_payments')
          .select('*').in('id', [...appliedHere.keys()])
        for (const p of pm ?? []) byId.set(p.id, p)
      }
      const merged: Any[] = [...byId.values()].map((p: Any) => ({
        ...p, applied_here: appliedHere.get(p.id) ?? Number(p.amount),
      }))
      merged.sort((a: Any, b: Any) =>
        String(b.payment_date ?? '').localeCompare(String(a.payment_date ?? '')))
      setPayments(merged)

      // 0229: the third route - paid through Project Finance, no invoice needed.
      const { data: fin } = await supabase.from('v_payable_finance_settlements')
        .select('*').eq('payable_id', id).order('paid_on', { ascending: false })
      setFinPays(fin ?? [])

      // Invoices already on file for this vendor that could be matched here.
      //
      // CONTRACTOR INVOICES ARE EXCLUDED, and this is not cosmetic. An invoice
      // carrying ra_bill_id belongs to a works contract, and matching one here
      // would call rpc_reconcile_invoice, which runs fn_recalc_payable, which
      // recomputes paid_amount from AGAINST_INVOICE payments only. A contractor
      // is paid AGAINST_RA_BILL, so a settled CPAY payable would silently go
      // back to OPEN with its payment forgotten. See migration 0140.
      const { data: inv } = await supabase.from('vendor_invoices')
        .select('*').eq('vendor_id', p.vendor_id).is('ra_bill_id', null)
        .order('invoice_date', { ascending: false }).limit(50)
      setOpenInvoices(inv ?? [])

      // The scans attached to those invoices, in one round trip rather than
      // one per row.
      const invIds = (inv ?? []).map((i: Any) => i.id)
      if (invIds.length) {
        const { data: files } = await supabase.from('document_attachments')
          .select('id, doc_id, file_name, kind, file_path, uploaded_at')
          .eq('doc_type', 'INVOICE').in('doc_id', invIds).order('uploaded_at')
        const grouped: Record<string, Any[]> = {}
        for (const f of (files ?? []) as Any[]) {
          (grouped[f.doc_id] ??= []).push(f)
        }
        setInvoiceFiles(grouped)
      } else {
        setInvoiceFiles({})
      }

      const { data: co } = await supabase.from('company_profile')
        .select('invoice_tolerance_pct').eq('id', 1).maybeSingle()
      if (co?.invoice_tolerance_pct != null) setTolerance(Number(co.invoice_tolerance_pct))

      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the payable')
    } finally { setLoading(false) }
  }, [id])

  useEffect(() => { void load() }, [load])

  const paymentFiles = useAttachmentsFor('PAYMENT', payments.map((p) => p.id))

  if (loading) return <Loading label="Loading payable…" />
  if (error || !payable) return <Alert tone="error">{error}</Alert>

  // The accrual is what the goods receipt was worth at purchase-order rates.
  // The settlement is what we will actually pay — the vendor's invoiced figure
  // once he has finished billing. They are different numbers on purpose.
  const accrual = Number(payable.payable_amount || 0)
  const invoiced = Number(payable.invoiced_amount || 0)
  const paid = Number(payable.paid_amount || 0)
  const advanceAdjusted = Number(payable.advance_adjusted || 0)
  const settlement = Number(payable.settlement_amount ?? accrual)
  const outstanding = Number(payable.outstanding_amount ?? Math.max(0, settlement - paid))
  const credited = Number(payable.credited_amount || 0)
  const variance = Number(payable.variance_amount || 0)
  const variancePct = Number(payable.variance_pct || 0)
  const closed = !!payable.invoicing_closed
  // A petty-cash purchase has no invoice and never will — the cash memo IS the
  // document. Showing "still to be billed" on one would send Accounts chasing an
  // invoice that is not coming.
  const inCash = !!(payable as { settled_in_cash?: boolean }).settled_in_cash
  const stillToInvoice = inCash ? 0 : Math.max(0, accrual - invoiced)
  // 0228: approved on or before 31-10-2026, so already inside the Accounts
  // opening balance that Project Finance pays from. Nothing is owed HERE.
  const inOpening = payable.status === 'IN_OPENING'
  const inOpeningAmount = Number((payable as { in_opening_amount?: number }).in_opening_amount || 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/payables" className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All payables</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{payable.id}</h1>
          <StatusPill status={payable.status} />
          {Number(payable.age_days) > payableOverdueDays && <span className="pill-red">{payable.age_days} days old</span>}
        </div>
        {maySettle && (
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary btn-sm" onClick={() => setInvoiceOpen(true)}>
              <Receipt className="h-4 w-4" /> Enter vendor invoice
            </button>
            <button className="btn-secondary btn-sm"
                    onClick={() => { setReconPreset(''); setReconOpen(true) }}
                    disabled={openInvoices.length === 0}>
              <Link2 className="h-4 w-4" /> Match an invoice
            </button>
          </div>
        )}
      </div>

      {!maySettle && (
        <Alert tone="info">
          You can see everything on this payable. Entering invoices and recording payments is done by the
          Procurement Executive or the Procurement Manager.
        </Alert>
      )}

      {/* ---------------------------------------------------------- summary */}
      {inCash && (
        <Alert tone="success" title="Paid at site from petty cash — no invoice is coming">
          The site paid for this material out of petty cash when it arrived. The cash memo
          attached to the goods receipt is the document; there is no vendor invoice to
          match, and nothing is owing.
        </Alert>
      )}
      {inOpening && (
        <Alert tone="info" title="Taken into the opening balance of 31-10-2026 — paid from Project Finance">
          This payable was approved on or before 31-10-2026, so it is already inside the Accounts
          liability sheet that Project Finance loaded as the opening balance (COB 31-10-2026).
          {inOpeningAmount > 0 && <> <strong>{rupees(inOpeningAmount)}</strong> moved there.</>}{' '}
          Nothing is owed on it here; the vendor is paid through a Finance payment requisition.
          Invoices may still be entered for the record.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="What is owed" className="lg:col-span-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <D label="Vendor" value={payable.vendor_name} />
            <D label="GSTIN" value={payable.vendor_gstin ?? '—'} />
            <D label="Site" value={payable.site_name ?? payable.site_id} />
            <D label="Against goods receipt" value={payable.grn_id ?? '—'}
               to={payable.grn_id ? `/grns/${payable.grn_id}` : undefined} />
            <D label="Purchase order" value={payable.po_id ?? '—'}
               to={payable.po_id ? `/pos/${payable.po_id}` : undefined} />
            <D label="Challan" value={payable.challan_no ?? '—'} />
            <D label="Raised on" value={ddmmyyyy(payable.generated_at)} />
            <D label="Received on" value={payable.received_date ? ddmmyyyy(payable.received_date) : '—'} />
            <D label="Due" value={payable.due_date ? ddmmyyyy(payable.due_date) : 'Per payment terms'} />
          </dl>
          {payable.remarks && (
            <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">{payable.remarks}</p>
          )}
        </Card>

        <Card title="Settlement">
          <dl className="space-y-2 text-sm">
            <Money label="Taxable (PO rates)" value={payable.taxable_amount} />
            <Money label="GST" value={payable.gst_amount} />
            <Money label="Receipt value" value={accrual} strong />
            <hr className="my-2" />
            <Money label="Invoiced by vendor" value={invoiced} tone={invoiced > 0 ? 'good' : undefined} />
            {credited > 0 && <Money label="Less credit notes" value={-credited} tone="good" />}
            {invoiced > 0 && Math.abs(variance) > 0.01 && (
              <Money label={`Price variance (${variancePct > 0 ? '+' : ''}${variancePct}%)`}
                     value={variance} tone={variance > 0 ? 'warn' : 'good'} />
            )}
            {!closed && !inCash && (
              <Money label="Still to be billed" value={stillToInvoice} tone={stillToInvoice > 0 ? 'warn' : 'good'} />
            )}
            <hr className="my-2" />
            <Money label="Amount to pay" value={settlement} strong />
            {/*
              Advance money is counted as paid, because it is — it left the bank
              before the goods arrived. Showing it on its own line means nobody
              has to wonder why "Paid" is non-zero on a delivery the accounts
              team has not yet settled.
            */}
            {advanceAdjusted > 0.005 && (
              <Money label="Settled from advance already paid" value={advanceAdjusted} tone="good" />
            )}
            <Money label={advanceAdjusted > 0.005 ? 'Paid (including the advance)' : 'Paid'}
                   value={paid} tone={paid > 0 ? 'good' : undefined} />
            {inOpening && inOpeningAmount > 0 && (
              <Money label="Taken into opening (Finance)" value={inOpeningAmount} />
            )}
            <Money label={outstanding < -0.01 ? 'Recoverable from vendor' : 'Outstanding'}
                   value={Math.abs(outstanding)} strong
                   tone={Math.abs(outstanding) > 0.01 ? 'warn' : 'good'} />
          </dl>

          {invoiced === 0 && (
            <p className="mt-3 text-xs text-slate-500">
              Valued at purchase-order rates from the accepted quantity. The vendor has not billed yet —
              enter his invoice, then match it here.
            </p>
          )}
          {invoiced > 0 && !closed && (
            <p className="mt-3 text-xs text-amber-700">
              Billing is still open: the invoiced total is more than {tolerance}% away from the receipt
              value, so the system will not assume the vendor has finished. Match the rest of his bills, or
              press <strong>Billing complete</strong> if this is all he is sending.
            </p>
          )}
          {closed && outstanding > 0.01 && (
            <p className="mt-3 text-xs text-slate-500">
              Fully billed. <strong>{rupees(settlement)}</strong> is payable — the vendor&rsquo;s invoiced
              figure, not the purchase-order estimate. Record each payment against the invoice below.
            </p>
          )}
          {closed && outstanding <= 0.01 && outstanding >= -0.01 && settlement > 0 && (
            <p className="mt-3 text-xs font-semibold text-emerald-700">Settled in full.</p>
          )}
          {outstanding < -0.01 && (
            <p className="mt-3 text-xs font-semibold text-amber-700">
              We have paid <strong>{rupees(Math.abs(outstanding))}</strong> more than is now due, because a
              credit note arrived after payment. Take it off this vendor&rsquo;s next bill.
            </p>
          )}

          {maySettle && invoiced > 0 && (
            <button className="btn-secondary btn-sm mt-3 w-full" disabled={busy}
                    onClick={() => {
                      if (closed) { void closeInvoicing(false); return }
                      // Closing short is a real decision — ask why.
                      if (invoiced < accrual - (accrual * tolerance / 100)) {
                        setCloseReason(''); setCloseOpen(true)
                      } else {
                        void closeInvoicing(true)
                      }
                    }}>
              {closed ? 'Reopen billing' : 'Billing complete — nothing more expected'}
            </button>
          )}
          {closed && payable.closure_reason && (
            <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
              Closed short: {payable.closure_reason}
            </p>
          )}
        </Card>
      </div>

      {/* ------------------------------------------------------- GRN detail */}
      {grnLines.length > 0 && (
        <Card title="What this payable is for"
              subtitle="Accepted quantity from the goods receipt — anything rejected was excluded before the payable was raised">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>#</th><th>Material</th><th className="num">Accepted</th><th className="num">Rejected</th>
                    <th className="num">Rate</th><th className="num">Amount</th><th>Rejection reason</th></tr>
              </thead>
              <tbody>
                {grnLines.map((l) => (
                  <tr key={l.id}>
                    <td className="text-xs text-slate-400">{l.sl_no}</td>
                    <td className="text-sm font-medium">{l.materials?.item_name ?? l.material_id}</td>
                    <td className="num font-semibold text-emerald-700">{l.quantity_accepted} {l.stock_uom}</td>
                    <td className="num text-red-600">{l.quantity_rejected || 0}</td>
                    <td className="num text-xs">{rupees(l.unit_rate)}/{l.purchase_uom}</td>
                    <td className="num text-xs">{rupees(l.quantity_accepted * l.conversion_factor * l.unit_rate)}</td>
                    <td className="max-w-[220px] truncate text-xs text-slate-500">{l.rejection_reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* --------------------------------------------------- the paperwork */}
      {payable.grn_id && (
        <AttachmentList docType="GRN" docId={payable.grn_id}
                        title="Goods receipt paperwork"
                        subtitle="The challan, e-way bill and photographs the store-man attached when the material arrived" />
      )}

      {/* The vendor's OWN papers, as opposed to this delivery's. Here because
          the bank mandate is checked at the moment of payment and nowhere
          else. Draws nothing for a role that may not read them. */}
      <VendorPapersCard vendorId={payable.vendor_id} vendorName={payable.vendor_name} />

      {/* ------------------------------------ invoices entered for this vendor */}
      <Card
        title="Vendor invoices on file"
        subtitle="Every invoice entered for this vendor, matched or not. An invoice has to be entered here before it can be matched against a payable."
      >
        {openInvoices.length === 0 && (
          <EmptyState
            icon={<Receipt className="h-8 w-8" />}
            title="No invoice entered for this vendor yet"
            message="Use Enter vendor invoice above to record the bill exactly as the vendor printed it."
          />
        )}

        {openInvoices.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Invoice</th><th>Invoice no.</th><th>Date</th>
                    <th className="num">Taxable</th><th className="num">GST</th><th className="num">Total</th>
                    <th className="num">Matched</th><th className="num">Unmatched</th>
                    <th>Scans</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {openInvoices.map((inv) => {
                  const matchedHere = recons.some((r) => r.invoice_id === inv.id)
                  const unmatched = Number(inv.total_amount || 0) - Number(inv.matched_amount || 0)
                  const files = invoiceFiles[inv.id] ?? []
                  return (
                    <tr key={inv.id} className={matchedHere ? 'bg-emerald-50/40' : ''}>
                      <td className="font-mono text-xs">
                        <Link to={`/invoices/${inv.id}`} className="text-oval-700 hover:underline">
                          {inv.id}
                        </Link>
                      </td>
                      <td className="text-sm font-medium">
                        <Link to={`/invoices/${inv.id}`} className="text-oval-700 hover:underline">
                          {inv.invoice_no}
                        </Link>
                      </td>
                      <td className="text-xs">{ddmmyyyy(inv.invoice_date)}</td>
                      <td className="num text-xs">{rupees(inv.taxable_amount)}</td>
                      <td className="num text-xs">{rupees(inv.gst_amount)}</td>
                      <td className="num text-xs font-semibold">{rupees(inv.total_amount)}</td>
                      <td className="num text-xs">{rupees(inv.matched_amount ?? 0)}</td>
                      <td className={`num text-xs ${unmatched > 0.01 ? 'font-semibold text-amber-700' : 'text-emerald-700'}`}>
                        {unmatched > 0.01 ? rupees(unmatched) : 'fully matched'}
                      </td>
                      <td>
                        {files.length === 0 && <span className="text-xs text-slate-400">—</span>}
                        <div className="flex flex-wrap gap-1">
                          {files.map((f) => (
                            <button key={f.id} type="button" onClick={() => void openFile(f.file_path)}
                                    title={f.file_name}
                                    className="inline-flex max-w-[140px] items-center gap-1 rounded border border-slate-200 px-1.5 py-0.5 text-xs text-oval-700 hover:bg-slate-50">
                              <Paperclip className="h-3 w-3 shrink-0" />
                              <span className="truncate">{f.file_name}</span>
                            </button>
                          ))}
                        </div>
                      </td>
                      <td><StatusPill status={inv.status ?? 'OPEN'} /></td>
                      <td className="text-right">
                        {matchedHere ? (
                          <span className="pill-green">Matched here</span>
                        ) : maySettle && unmatched > 0.01 ? (
                          <button className="btn-secondary btn-sm"
                                  onClick={() => { setReconPreset(inv.id); setReconOpen(true) }}>
                            <Link2 className="h-3 w-3" /> Match to this payable
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------- invoices */}
      <Card title="Vendor invoices matched to this payable"
            subtitle="The three-way match: purchase order, goods receipt, vendor invoice">
        {recons.length === 0 && (
          <EmptyState icon={<Receipt className="h-8 w-8" />} title="No invoice matched yet"
                      message="Enter the vendor's tax invoice, then match it against this payable." />
        )}
        {recons.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Invoice</th><th>Invoice no.</th><th>Date</th>
                    <th className="num">Invoice total</th><th className="num">Matched here</th>
                    <th className="num">Share of this payable</th><th>Note</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {recons.map((r) => {
                  const inv = r.vendor_invoices ?? {}
                  // Deliberately NOT the stored per-row variance. That figure is
                  // matched-minus-accrual, which is meaningless when a vendor
                  // bills one delivery across three invoices — it reports a
                  // three-lakh "variance" on a payable that is perfectly settled.
                  // The real variance is a payable-level number and lives once,
                  // in the Settlement card above.
                  const share = accrual > 0 ? (Number(r.matched_amount) / accrual) * 100 : 0
                  return (
                    <tr key={r.id}>
                      <td className="font-mono text-xs">
                        <Link to={`/invoices/${r.invoice_id}`} className="text-oval-700 hover:underline">
                          {r.invoice_id}
                        </Link>
                      </td>
                      <td className="text-sm font-medium">{inv.invoice_no ?? '—'}</td>
                      <td className="text-xs">{inv.invoice_date ? ddmmyyyy(inv.invoice_date) : '—'}</td>
                      <td className="num text-xs">{rupees(inv.total_amount ?? 0)}</td>
                      <td className="num text-xs font-semibold">{rupees(r.matched_amount)}</td>
                      <td className="num text-xs">
                        {share >= 99.99
                          ? <span className="font-semibold text-emerald-700">the whole payable</span>
                          : <span className="text-slate-600">{share.toFixed(1)}%</span>}
                      </td>
                      <td className="max-w-[240px] truncate text-xs text-slate-500"
                          title={r.override_reason ?? r.variance_reason ?? ''}>
                        {r.tolerance_override && (
                          <span className="pill-amber mr-1">approved over tolerance</span>
                        )}
                        {r.variance_reason ?? (recons.length > 1 ? 'part of a split bill' : '—')}
                      </td>
                      <td><StatusPill status={inv.status ?? 'OPEN'} /></td>
                      <td className="text-right">
                        {maySettle && (
                          <button className="btn-secondary btn-sm"
                                  onClick={() => setPayOpen({ ...inv, invoice_id: r.invoice_id })}>
                            <BadgeIndianRupee className="h-3 w-3" /> Record payment
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* -------------------------------------------------------- payments */}
      <Card title="Payments made"
            subtitle="Recorded here after the accounts team has actually paid — this system does not move money">
        {payments.length === 0 && finPays.length === 0 && (
          <p className="text-sm text-slate-500">Nothing has been paid against this payable yet.</p>
        )}
        {payments.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Payment</th><th>How it reached this payable</th><th>Date</th><th>Mode</th>
                    <th>Reference</th><th className="num">Applied here</th><th>Proof</th><th>Remarks</th></tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  // Petty cash is not "a payment Accounts made" — the site
                  // spent its own float at the counter. Saying so here stops
                  // somebody chasing a bank reference that never existed.
                  const cash = p.payment_type === 'PETTY_CASH'
                  const part = Number(p.applied_here) < Number(p.amount)
                  return (
                    <tr key={p.id}>
                      <td className="font-mono text-xs">{p.id}</td>
                      <td className="text-xs">
                        {cash ? (
                          <span className="text-amber-700">Site petty cash, at the counter</span>
                        ) : p.invoice_id ? (
                          <span className="font-mono text-slate-500">{p.invoice_id}</span>
                        ) : (
                          <span className="text-slate-600">Advance applied to this payable</span>
                        )}
                      </td>
                      <td className="text-xs">{ddmmyyyy(p.payment_date)}</td>
                      <td className="text-xs">{cash ? 'CASH' : p.mode}</td>
                      <td className="font-mono text-xs">{p.reference_no ?? '—'}</td>
                      <td className="num text-sm font-semibold text-emerald-700">
                        {rupees(p.applied_here)}
                        {part && (
                          <span className="block text-[11px] font-normal text-slate-500">
                            of {rupees(p.amount)} paid
                          </span>
                        )}
                      </td>
                      <td><FileChips files={paymentFiles[p.id]} /></td>
                      <td className="max-w-[200px] truncate text-xs text-slate-500">{p.remarks ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {finPays.length > 0 && (
          <div className={payments.length > 0 ? 'mt-4' : undefined}>
            <p className="mb-1 text-xs font-semibold text-slate-600">Paid through Project Finance</p>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr><th>Finance payment</th><th>How</th><th>Date</th><th className="num">Applied here</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {finPays.map((f) => (
                    <tr key={f.id} className={f.reversed_at ? 'opacity-50' : undefined}>
                      <td className="font-mono text-xs">{f.release_no}</td>
                      <td className="text-xs">{f.kind === 'NEW' ? 'Payment requisition' : 'Finance advance adjusted here'}</td>
                      <td className="text-xs">{ddmmyyyy(f.paid_on)}</td>
                      <td className="num text-sm font-semibold text-emerald-700">{rupees(f.amount)}</td>
                      <td className="text-xs">{f.reversed_at ? `Reversed ${ddmmyyyy(f.reversed_at)}` : 'Paid'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <Modal
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        title="Close billing on this payable?"
        subtitle={`Billed ${rupees(invoiced)} against a goods receipt worth ${rupees(accrual)}`}
        footer={<>
          <button className="btn-secondary" onClick={() => setCloseOpen(false)} disabled={busy}>Cancel</button>
          <button className="btn-primary" disabled={busy || !closeReason.trim()}
                  onClick={async () => { await closeInvoicing(true, closeReason); setCloseOpen(false) }}>
            Close billing
          </button>
        </>}
      >
        <Alert tone="warning" title="This says the vendor will never bill the rest">
          The amount payable becomes <strong>{rupees(invoiced)}</strong> instead of {rupees(accrual)}.
          We stop expecting an invoice for the difference of {rupees(Math.max(0, accrual - invoiced))}.
        </Alert>
        <div className="mt-4">
          <Field label="Why is nothing more expected?" required
                 hint="A short delivery he never made up, a negotiated discount, an agreed write-off — say which.">
            <Textarea rows={2} value={closeReason} onChange={(e) => setCloseReason(e.target.value)}
                      placeholder="e.g. Vendor confirmed on 12 Aug he will not supply the balance 40 bags; no further invoice due." />
          </Field>
        </div>
      </Modal>

      {invoiceOpen && (
        <InvoiceModal
          payable={payable}
          busy={busy} setBusy={setBusy}
          onClose={() => setInvoiceOpen(false)}
          onDone={async () => { setInvoiceOpen(false); await load() }}
          toast={toast}
        />
      )}

      {reconOpen && (
        <ReconcileModal
          payable={payable} invoices={openInvoices} preset={reconPreset}
          tolerance={tolerance} isHead={role === 'procurement_head' || role === 'admin'}
          alreadyMatchedHere={invoiced}
          busy={busy} setBusy={setBusy}
          onClose={() => { setReconOpen(false); setReconPreset('') }}
          onDone={async () => { setReconOpen(false); setReconPreset(''); await load() }}
          toast={toast}
        />
      )}

      {payOpen && (
        <PaymentModal
          invoice={payOpen}
          busy={busy} setBusy={setBusy}
          onClose={() => setPayOpen(null)}
          onDone={async () => { setPayOpen(null); await load() }}
          toast={toast}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
function InvoiceModal({ payable, busy, setBusy, onClose, onDone, toast }: Any) {
  const [invoiceNo, setInvoiceNo] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(iso(new Date()))
  const [taxable, setTaxable] = useState(String(payable.taxable_amount ?? ''))
  const [gst, setGst] = useState(String(payable.gst_amount ?? ''))
  const [remarks, setRemarks] = useState('')
  const [files, setFiles] = useState<PendingAttachment[]>([])

  const total = (Number(taxable) || 0) + (Number(gst) || 0)

  async function save() {
    if (!invoiceNo.trim()) { toast.error("Enter the vendor's invoice number."); return }
    if (!(Number(taxable) > 0)) { toast.error('The taxable amount must be greater than zero.'); return }
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_record_vendor_invoice', {
        p_vendor_id: payable.vendor_id,
        p_invoice_no: invoiceNo.trim(),
        p_invoice_date: invoiceDate,
        p_taxable_amount: Number(taxable),
        p_gst_amount: Number(gst) || 0,
        p_po_id: payable.po_id ?? null,
        p_remarks: remarks || null,
      })
      if (error) throw new Error(error.message)
      if (files.length) await queueAttachments('INVOICE', data as string, files)
      toast.success(`Invoice ${invoiceNo} recorded as ${data}. Now match it against this payable.`)
      await onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not record the invoice')
    } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} wide title="Enter the vendor's tax invoice"
           subtitle={`${payable.vendor_name} · against ${payable.grn_id ?? payable.id}`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Record invoice</button>
           </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Invoice number" required hint="Exactly as printed on the vendor's bill">
          <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} autoFocus />
        </Field>
        <DocumentDate label="Invoice date" value={invoiceDate} onChange={setInvoiceDate}
                      hint="The date printed on the vendor's bill, not today." />
        <Field label="Taxable amount (₹)" required hint="Pre-filled from the payable — correct it to match the bill">
          <Input type="number" step="any" value={taxable} onChange={(e) => setTaxable(e.target.value)} />
        </Field>
        <Field label="GST amount (₹)">
          <Input type="number" step="any" value={gst} onChange={(e) => setGst(e.target.value)} />
        </Field>
        <Field label="Remarks" className="sm:col-span-2">
          <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Field>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 p-3 text-sm">
        <div className="flex justify-between"><span>Invoice total</span><strong>{rupees(total)}</strong></div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>Payable raised from the goods receipt</span><span>{rupees(payable.payable_amount)}</span>
        </div>
        {Math.abs(total - Number(payable.payable_amount)) > 0.01 && (
          <p className="mt-2 text-xs font-semibold text-amber-700">
            These do not agree. You will have to explain the difference when you match it.
          </p>
        )}
      </div>

      <div className="mt-4">
        <AttachmentCollector docType="INVOICE" value={files} onChange={setFiles}
                             hint="Attach a scan or photograph of the invoice" />
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
function ReconcileModal({ payable, invoices, preset, tolerance, isHead, alreadyMatchedHere,
                         busy, setBusy, onClose, onDone, toast }: Any) {
  const [invoiceId, setInvoiceId] = useState<string>(preset ?? '')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [override, setOverride] = useState('')

  const invoice = useMemo(() => invoices.find((i: Any) => i.id === invoiceId), [invoices, invoiceId])
  const accrual = Number(payable.payable_amount || 0)

  // What is left to match, from both ends: how much of this payable is not yet
  // billed, and how much of this invoice is not yet allocated anywhere.
  const payableLeft = Math.max(0, accrual - Number(alreadyMatchedHere || 0))
  const invoiceLeft = invoice
    ? Math.max(0, Number(invoice.total_amount || 0) - Number(invoice.matched_amount || 0))
    : 0

  // Offer the sensible figure rather than the whole payable — a vendor billing
  // one delivery across three lorries should not have to retype the amount.
  useEffect(() => {
    if (!invoice) return
    const suggestion = Math.min(invoiceLeft || 0, payableLeft || 0) || invoiceLeft
    setAmount(suggestion > 0 ? String(Number(suggestion.toFixed(2))) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId])
  const cumulative = Number(alreadyMatchedHere || 0) + (Number(amount) || 0)
  const variance = cumulative - accrual
  // Billing less than the receipt is an instalment, not a discrepancy. Only
  // billing MORE needs an explanation.
  const needsReason = variance > 0.01
  // Above the company tolerance only the Head of Procurement may proceed.
  const ceiling = accrual * (1 + Number(tolerance || 5) / 100)
  const overTolerance = (Number(amount) || 0) > ceiling + 0.01

  async function save() {
    if (!invoiceId) { toast.error('Choose the invoice to match.'); return }
    if (!(Number(amount) > 0)) { toast.error('The matched amount must be greater than zero.'); return }
    if (needsReason && !reason.trim()) {
      toast.error('The vendor has now billed more than the goods receipt was worth. Explain why.'); return
    }
    if (overTolerance && !isHead) {
      toast.error(`This is more than ${tolerance}% above the receipt value. Only the Procurement Manager can approve that.`); return
    }
    if (overTolerance && !override.trim()) {
      toast.error('Record why you are approving an amount beyond the tolerance.'); return
    }
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_reconcile_invoice', {
        p_invoice_id: invoiceId,
        p_payable_id: payable.id,
        p_matched_amount: Number(amount),
        p_variance_reason: reason || null,
        p_override_reason: override || null,
      })
      if (error) throw new Error(error.message)
      toast.success('Invoice matched against this payable.')
      await onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not match the invoice')
    } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} wide title="Match an invoice to this payable"
           subtitle={`${payable.vendor_name} · payable ${rupees(payable.payable_amount)}`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy || (overTolerance && !isHead)}>
               {overTolerance && isHead ? 'Approve and match' : 'Match invoice'}
             </button>
           </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Vendor invoice" required className="sm:col-span-2">
          <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
                  placeholder="Choose an invoice already on file…"
                  options={invoices.map((i: Any) => ({
                    value: i.id,
                    label: `${i.invoice_no} · ${ddmmyyyy(i.invoice_date)} · ${rupees(i.total_amount)}`,
                  }))} />
        </Field>
        <Field label="Amount to match against this payable (₹)" required
               hint="Usually the whole payable. Match less if one invoice covers several receipts.">
          <Input type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <div className="rounded-lg border border-slate-200 p-3 text-sm">
          <div className="flex justify-between">
            <span>Goods receipt is worth</span><span>{rupees(accrual)}</span>
          </div>
          {Number(alreadyMatchedHere || 0) > 0.01 && (
            <div className="flex justify-between text-slate-500">
              <span>Already billed on other invoices</span><span>{rupees(alreadyMatchedHere)}</span>
            </div>
          )}
          <div className="flex justify-between"><span>Matching now</span><span>{rupees(Number(amount) || 0)}</span></div>
          <hr className="my-1.5" />
          <div className={`flex justify-between font-semibold ${
            variance > 0.01 ? 'text-red-600' : variance < -0.01 ? 'text-slate-600' : 'text-emerald-700'}`}>
            <span>{variance < -0.01 ? 'Still to be billed after this' : 'Billed in total'}</span>
            <span>{variance < -0.01 ? rupees(Math.abs(variance)) : rupees(cumulative)}</span>
          </div>
          {variance > 0.01 && (
            <p className="mt-1 text-xs font-semibold text-red-600">
              {rupees(variance)} more than the goods receipt was worth.
            </p>
          )}
          {variance < -0.01 && (
            <p className="mt-1 text-xs text-slate-500">
              Normal if more of his bills are still to come. If this is his last one, use
              <strong> Billing complete</strong> on the payable afterwards.
            </p>
          )}
        </div>
        {needsReason && (
          <Field label="The vendor has billed more than we received. Why?" required className="sm:col-span-2"
                 hint="This is recorded permanently and is what an auditor will read.">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Vendor billed freight of ₹1,200 not on the purchase order — approved by Procurement Manager" />
          </Field>
        )}
        {overTolerance && (
          <div className="sm:col-span-2">
            {isHead ? (
              <>
                <Alert tone="warning" title={`This is beyond the ${tolerance}% tolerance`}>
                  You are approving <strong>{rupees(Number(amount) || 0)}</strong> against a receipt worth{' '}
                  <strong>{rupees(accrual)}</strong> at purchase-order rates. As Procurement Manager you may
                  do this, but it is recorded against your name permanently.
                </Alert>
                <div className="mt-3">
                  <Field label="Your approval, in writing" required
                         hint="What justifies paying above the ordered rate? Name who agreed it and when.">
                    <Textarea rows={2} value={override} onChange={(e) => setOverride(e.target.value)}
                              placeholder="e.g. Cement rate rose ₹380 → ₹410 after the September revision. Confirmed with vendor 12 Sep; no alternative supply available before the pour." />
                  </Field>
                </div>
              </>
            ) : (
              <Alert tone="error" title={`Beyond the ${tolerance}% tolerance`}>
                This bills <strong>{rupees(Number(amount) || 0)}</strong> against a receipt worth{' '}
                <strong>{rupees(accrual)}</strong>. Ask the Procurement Manager to approve it, or ask the
                vendor for a corrected invoice.
              </Alert>
            )}
          </div>
        )}
        {invoice && (
          <div className="sm:col-span-2">
            <Alert tone="info">
              Invoice {invoice.invoice_no} totals {rupees(invoice.total_amount)}, of which{' '}
              {rupees(invoice.matched_amount ?? 0)} is already matched elsewhere.
            </Alert>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
function PaymentModal({ invoice, busy, setBusy, onClose, onDone, toast }: Any) {
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(iso(new Date()))
  const [mode, setMode] = useState('NEFT')
  const [reference, setReference] = useState('')
  const [remarks, setRemarks] = useState('')
  const [files, setFiles] = useState<PendingAttachment[]>([])

  async function save() {
    if (!(Number(amount) > 0)) { toast.error('Enter the amount paid.'); return }
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_record_payment', {
        p_invoice_id: invoice.invoice_id ?? invoice.id,
        p_amount: Number(amount),
        p_payment_date: date,
        p_mode: mode,
        p_reference_no: reference || null,
        p_remarks: remarks || null,
      })
      if (error) throw new Error(error.message)
      if (files.length) await queueAttachments('PAYMENT', data as string, files)
      toast.success(`Payment ${data} recorded.`)
      await onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not record the payment')
    } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="Record a payment"
           subtitle={`Against invoice ${invoice.invoice_no ?? invoice.invoice_id}`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Record payment</button>
           </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Amount paid (₹)" required>
          <Input type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </Field>
        <DocumentDate label="Payment date" value={date} onChange={setDate}
                      hint="The day the money actually left the bank." />
        <Field label="Mode" required>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}
                  options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))} />
        </Field>
        <Field label="Reference number" hint="UTR, cheque number, transaction id">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Remarks" className="sm:col-span-2">
          <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Field>
      </div>
      <div className="mt-4">
        <AttachmentCollector docType="PAYMENT" value={files} onChange={setFiles}
                             hint="Attach the bank screenshot, the UTR advice or the paid cheque. This is the proof the money left." />
      </div>

      <div className="mt-4">
        <Alert tone="warning">
          This records a payment the accounts team has already made. It does not send money anywhere.
          Enter it only once the transfer has actually gone out.
        </Alert>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
const D = ({ label, value, to }: { label: string; value: string; to?: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-slate-800">
      {to ? <Link to={to} className="font-mono text-sm text-oval-700 hover:underline">{value}</Link> : value}
    </dd>
  </div>
)

const Money = ({ label, value, strong, tone }: {
  label: string; value: number | string; strong?: boolean; tone?: 'good' | 'warn'
}) => (
  <div className="flex items-center justify-between">
    <dt className="text-slate-600">{label}</dt>
    <dd className={[
      strong ? 'font-bold' : '',
      tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-slate-800',
    ].join(' ')}>{rupees(Number(value) || 0)}</dd>
  </div>
)

